/**
 * utils/contactImportParser.js
 * Pure parser (batch 1 of 5 for the contacts Excel import): turns already-extracted xlsx rows
 * (SheetJS `sheet_to_json(ws, {header:1, raw:true})` shape) into structured shop rows per sheet,
 * plus a flat list of problems found. No React, no I/O, no commit, no wizard — see
 * `importParser.js`'s own header comment for the identical discipline applied to the (unrelated)
 * catalog import; this file is the same discipline for a different, unrelated workbook.
 *
 * Input contract: `sheets = [{ name: string, rows: any[][] }]`. Exactly two sheets matter,
 * matched by name (trim + case-insensitive) — every other sheet in the workbook is ignored:
 *   - CABANG (0-based columns): 0 Nama Toko · 1 Alamat Toko · 2 Sales · 3 Ekspedisi ·
 *     4 Contact Person · 5 Email · 6 Keterangan
 *   - PUSAT: the same 7 columns plus 7 NPWP
 * Row 1 (rows[0]) is always the header row — fixed position, not scanned/detected the way
 * importParser.js's catalog sheets are (this workbook's layout doesn't repeat headers mid-sheet
 * or vary column position across sheets).
 *
 * **Blank-name-row attachment (the highest-risk part of this module)**: a data row with a blank
 * Nama Toko belongs to the shop named by the nearest row ABOVE it that has one — a fourth row
 * classification alongside "named shop row" / "fully blank row" / "unattachable (orphan)".
 * Every non-Sales column on such a row is genuine EXTRA data for that parent shop (an additional
 * ekspedisi, an additional address, ...) — this is why every field below except `name`/`sales`
 * is an array: a shop can pick up more than one value per field across its attached blank rows.
 * `sales` on a blank row is the ONE special case: it is a spreadsheet fill-down artefact, never
 * real data — it is never stored, only compared against the parent's own `sales` to raise a
 * `possible-second-sales` problem on disagreement. **This is not a defect to fix — a real,
 * confirmed case (CABANG row 225) turned out to be one shop with two genuine salespeople, its
 * "extra address" just the same address typed without the "JL" prefix.** This module never
 * resolves it (never merges, never picks one) — it only flags it for a human to CONFIRM in a
 * later review batch. A blank-name row with no parent above it (nothing to attach to) is
 * unattachable — its data is dropped and reported, never guessed onto some other shop.
 *
 * City has no column — `deriveCity` takes the text after the LAST comma of a shop's primary
 * (first) address. This works well when an address genuinely ends "...,  CITY" and fails openly
 * (`derived:false`) when there's no comma at all (common on PUSAT, whose tail is often a
 * province+postcode) — never guessed, never silently stored as if a human chose it. A later
 * review step (a different batch) is what lets a human correct/confirm it.
 *
 * NPWP (PUSAT only) and every free-text field are stored EXACTLY as typed — never trimmed
 * beyond whitespace, reformatted, or validated (same rule this app already applies to
 * `Contact.npwp` everywhere else). `contactPerson` holds phone numbers under a misleading
 * header in the source file — parsed as given, never moved/reformatted/validated here.
 *
 * Duplicate-shop-name detection is a NAME match only (trim + collapsed whitespace + uppercase),
 * not a fuzzy one — deliberately narrower than `contactMatch.js`'s `contactMatchScore`, whose
 * similarity/containment/prefix heuristics exist to SUGGEST possibly-the-same-firm across
 * genuinely different names (a later batch's job); here the names are already identical, and
 * this module simply classifies whether the addresses on the duplicate rows also happen to
 * agree or not. `contactMatchScore`/`normalizePhone` have no call site in this batch — parsing
 * has no live contacts to score against yet; both are read and reused starting from the batch
 * that actually resolves duplicates / suggests business links against the parsed rows.
 *
 * "Sales" is reported here (`stats.salesUsage`, per sheet) only as distinct raw values (merged
 * case-insensitively — "BUDI"/"Budi" are one name) and how many SHOPS use each — never mapped
 * to a real Person id, that is a later batch's job. A shop's own `sales` field is stored exactly
 * as typed on ITS named row only, never touched by a blank row's fill-down value.
 *
 * Structure vs. content (this module's central discipline): a MISSING sheet or a column set
 * that doesn't match — wrong names, wrong order, wrong count — makes a sheet impossible to
 * parse safely at all; that sheet's `shops` comes back empty and the failure is reported as a
 * `severity:"error"` problem naming exactly what was expected vs. found. Every other problem
 * (a blank shop, an unusable Sales value, a city that can't be derived, a duplicate name, an
 * unattachable blank row, ...) is `severity:"warning"` — a real data-quality issue to surface,
 * never a reason to refuse the file.
 */

/**
 * True for a cell shaped like SheetJS's own error-cell descriptor (`{t:"e", v, w}`) — the ONE
 * shape a `sheet_to_json(ws, {header:1, ...})` row array cannot preserve on its own. Confirmed
 * against xlsx 0.18.5's own `make_json_row`: an error-type cell's value is discarded (`v = v==0
 * ? null : void 0`) BEFORE the `raw`/`defval` options are ever consulted, so a real Excel error
 * (a broken "#N/A" formula, say) collapses to a bare `null`/`undefined` — indistinguishable from
 * a genuinely empty cell — under EVERY combination of those options. A caller that wants this
 * module to tell the two apart must substitute the RAW worksheet cell (`ws[address]`) at that
 * position instead of trusting `sheet_to_json`'s own collapsed value; this function (and
 * `cellText` below, which treats an error cell as blank everywhere else) is what receives that
 * shape when a caller does.
 */
function isErrorCell(v) {
  return v != null && typeof v === "object" && v.t === "e";
}

/** blank(v): trim only. Deliberately NOT the catalog parser's "-" convention — that convention
 *  is specific to the fill-down-formula export this file has nothing to do with, and nothing in
 *  this workbook's measured facts mentions "-" as a null marker; treating it as blank here would
 *  be a guess. An error-shaped cell (above) is also blank here — never stringified into
 *  "[object Object]" — but Sales specifically evaluates it BEFORE this collapse so the original
 *  error text can still be reported (see `evaluateSalesCell`). */
function cellText(v) {
  if (isErrorCell(v)) return "";
  return v == null ? "" : String(v).trim();
}
function isBlank(v) {
  return cellText(v) === "";
}

/** Trivial local normalizer for name grouping — this app already carries five near-identical
 *  private `norm(s)` helpers (contacts.js, businesses.js, branches.js, persons.js,
 *  salespeople.js); this is the same trivial one-liner, kept local rather than shared for the
 *  same reason those five are: it's a single `trim/whitespace/case` line, not worth a shared
 *  module and its own import surface. */
function normalizeShopName(s) {
  return cellText(s).replace(/\s+/g, " ").toUpperCase();
}

const CABANG_COLUMNS = ["Nama Toko", "Alamat Toko", "Sales", "Ekspedisi", "Contact Person", "Email", "Keterangan"];
const PUSAT_COLUMNS = [...CABANG_COLUMNS, "NPWP"];

/** Sheet name -> its expected column layout, checked in this exact order. */
const SHEET_DEFS = { CABANG: CABANG_COLUMNS, PUSAT: PUSAT_COLUMNS };

/** A LITERAL string a human typed that happens to spell a broken-formula token (someone typing
 *  "#N/A" as text, meaning "no data") — one of several ways a Sales cell can end up unusable,
 *  never the only one `evaluateSalesCell` checks for (see that function: a real Excel ERROR cell
 *  never reaches this regex at all, since it's caught by shape, not by string match). */
const BAD_SALES_TOKEN = /^#(N\/A|REF!|VALUE!|DIV\/0!|NAME\?|NULL!|NUM!)$/i;

/**
 * Evaluates a Sales cell for USABILITY — the one rule this module applies, regardless of WHAT
 * shape makes a cell unusable: a genuinely blank cell, a real Excel error (the `{t:"e",...}`
 * shape `isErrorCell` catches), or a literal broken-formula string a human typed by hand (e.g.
 * "#N/A" as text) all fail the exact same rule. Never invents a value; `found` always carries
 * exactly what was actually there so a later review step (a different batch) can ask a human for
 * the real one instead of guessing.
 * `stored` is the value that becomes `shop.sales` — always `""` when `usable` is false,
 * regardless of reason: an unusable value is not a Sales name a later batch should ever map to a
 * real Person, so it must never leak into `shop.sales` or the `salesUsage` report just because it
 * happened to be a real (if garbage) string. The original, unmodified text a human could act on
 * lives ONLY in `found` — on the problem this evaluation raises, never on the shop itself.
 * @returns {{usable: boolean, stored: string, reason: "blank"|"error-cell"|"bad-token"|null, found: string|null}}
 */
function evaluateSalesCell(raw) {
  if (isErrorCell(raw)) {
    return { usable: false, stored: "", reason: "error-cell", found: raw.w || `#ERROR(${raw.v})` };
  }
  const text = cellText(raw);
  if (text === "") {
    return { usable: false, stored: "", reason: "blank", found: null };
  }
  if (BAD_SALES_TOKEN.test(text)) {
    return { usable: false, stored: "", reason: "bad-token", found: text };
  }
  return { usable: true, stored: text, reason: null, found: text };
}

/**
 * `address`'s text after its LAST comma, trimmed. `derived:false` (never a guessed value) in
 * three cases — never a guessed value in any of them: there's no comma at all; the address
 * itself is blank; or the tail contains a digit. **A tail containing a digit is never treated as
 * a city** (confirmed against the real file: of PUSAT's "derived" tails before this rule, 39 were
 * a bare postcode — "28292" — and 54 were "PROVINCE POSTCODE" — "KALIMANTAN SELATAN 72221" — both
 * shapes a comma-based split cannot tell apart from a real city on its own; a genuine Indonesian
 * city name never contains a digit). This is a rule about CONTENT, not about which sheet the row
 * came from — PUSAT's tail happens to be a province+postcode far more often than CABANG's, but
 * nothing here reads `sheet` or branches on it; the same address text derives the same way on
 * either sheet, and would on any other file shaped like this one too.
 * @returns {{value: string|null, derived: boolean, source: string|null}}
 */
export function deriveCity(address) {
  const trimmed = cellText(address);
  if (!trimmed) return { value: null, derived: false, source: null };
  const idx = trimmed.lastIndexOf(",");
  if (idx === -1) return { value: null, derived: false, source: null };
  const tail = trimmed.slice(idx + 1).trim();
  if (!tail) return { value: null, derived: false, source: null };
  if (/\d/.test(tail)) return { value: null, derived: false, source: null };
  return { value: tail, derived: true, source: "address-tail-after-last-comma" };
}

/** Compares a header row against the expected column list: same names (case-insensitive/trim),
 *  same order, same count — a trailing extra header cell also counts as a mismatch. Returns the
 *  actually-found cells (plus one extra, if any) so a `column-mismatch` problem can show exactly
 *  what was expected vs. what was there. */
function checkHeader(headerRow, expectedColumns) {
  const row = headerRow || [];
  const actual = expectedColumns.map((_, i) => cellText(row[i]));
  const namesOk = actual.every((cell, i) => cell.toLowerCase() === expectedColumns[i].toLowerCase());
  const extraCell = cellText(row[expectedColumns.length]);
  if (extraCell) actual.push(extraCell);
  return { ok: namesOk && !extraCell, actual };
}

/** Parses one already-name-matched sheet. `columns` is CABANG_COLUMNS or PUSAT_COLUMNS (the
 *  latter's extra length is what turns on NPWP handling). */
function parseContactSheet(sheetName, rows, columns) {
  const problems = [];
  const header = (rows && rows[0]) || [];
  const { ok, actual } = checkHeader(header, columns);
  if (!ok) {
    problems.push({
      severity: "error",
      type: "column-mismatch",
      sheet: sheetName,
      expected: columns,
      actual,
      message: `Kolom pada sheet "${sheetName}" tidak sesuai — diharapkan [${columns.join(", ")}], ditemukan [${actual.join(", ")}].`,
    });
    return { shops: null, problems, stats: null };
  }

  const hasNpwp = columns.length > CABANG_COLUMNS.length;
  const shops = [];
  let current = null;
  let blankRowCount = 0;
  let orphanBlankRowCount = 0;
  let missingAddressCount = 0;
  let unusableSalesCount = 0;
  const unusableSalesByReason = { blank: 0, errorCell: 0, badToken: 0 };
  const extras = { addresses: 0, ekspedisi: 0, contactPerson: 0, email: 0, keterangan: 0, npwp: 0 };

  const dataRows = (rows || []).slice(1);
  for (let i = 0; i < dataRows.length; i++) {
    const row = dataRows[i] || [];
    const excelRow = i + 2; // rows[0] is Excel row 1 (the header); dataRows[0] is Excel row 2.

    const name = cellText(row[0]);
    const address = cellText(row[1]);
    const salesEval = evaluateSalesCell(row[2]);
    const sales = salesEval.stored;
    const ekspedisi = cellText(row[3]);
    const contactPerson = cellText(row[4]);
    const email = cellText(row[5]);
    const keterangan = cellText(row[6]);
    const npwp = hasNpwp ? cellText(row[7]) : "";

    const entirelyBlank = !name && !address && !sales && !ekspedisi && !contactPerson && !email && !keterangan && !npwp;
    if (entirelyBlank) continue;

    if (isBlank(name)) {
      if (!current) {
        orphanBlankRowCount += 1;
        const fields = [];
        if (address) fields.push("Alamat Toko");
        if (sales) fields.push("Sales");
        if (ekspedisi) fields.push("Ekspedisi");
        if (contactPerson) fields.push("Contact Person");
        if (email) fields.push("Email");
        if (keterangan) fields.push("Keterangan");
        if (npwp) fields.push("NPWP");
        problems.push({
          severity: "warning",
          type: "orphan-blank-row",
          sheet: sheetName,
          row: excelRow,
          fields,
          message: `Baris ${excelRow} pada sheet "${sheetName}" tidak punya Nama Toko dan tidak ada baris toko sebelumnya untuk menampung datanya (kolom terisi: ${fields.join(", ") || "tidak ada"}).`,
        });
        continue;
      }

      blankRowCount += 1;
      current.sourceRows.push(excelRow);

      if (sales) {
        const a = sales.replace(/\s+/g, " ").toUpperCase();
        const b = (current.sales || "").replace(/\s+/g, " ").toUpperCase();
        if (a !== b) {
          // Not a fill-down error to fix — a differing Sales here is a POSSIBLE SECOND
          // SALESPERSON for the same shop (confirmed against the real file: CABANG row 225 is
          // exactly one shop with two salespeople, its "extra address" just the same address
          // typed without the "JL" prefix). This is a real decision, resolved by
          // `contactImportResolve.js` (see that file's own `possible-second-sales` handling) —
          // never a defect this module resolves or a value it overwrites.
          //
          // `parent*`/`row*` carry BOTH rows' own address/ekspedisi/contactPerson (not just
          // Sales) so the resolution layer's own review UI can show them side by side, the exact
          // "why am I being asked" comparison step 2's duplicate-name review already gives a
          // human for a structurally similar decision. `current.addresses[0]`/`.ekspedisi[0]`/
          // `.contactPerson[0]` are always the PARENT's own original values — nothing is ever
          // unshifted onto these arrays, only pushed after, so index 0 stays the parent's own
          // contribution regardless of how many other continuation rows have added to it since.
          problems.push({
            severity: "warning",
            type: "possible-second-sales",
            sheet: sheetName,
            row: excelRow,
            shopName: current.name,
            parentRow: current.row,
            parentSales: current.sales,
            rowSales: sales,
            parentAddress: current.addresses[0] || "",
            rowAddress: address || "",
            parentEkspedisi: current.ekspedisi[0] || "",
            rowEkspedisi: ekspedisi || "",
            parentContactPerson: current.contactPerson[0] || "",
            rowContactPerson: contactPerson || "",
            message: `Baris ${excelRow} (toko "${current.name}") punya Sales "${sales}", berbeda dari Sales induknya di baris ${current.row} ("${current.sales}") — mungkin toko ini punya dua sales, perlu dikonfirmasi.`,
          });
        }
      }
      if (address) { current.addresses.push(address); extras.addresses += 1; }
      if (ekspedisi) { current.ekspedisi.push(ekspedisi); extras.ekspedisi += 1; }
      if (contactPerson) { current.contactPerson.push(contactPerson); extras.contactPerson += 1; }
      if (email) { current.email.push(email); extras.email += 1; }
      if (keterangan) { current.keterangan.push(keterangan); extras.keterangan += 1; }
      if (hasNpwp && npwp) { current.npwp.push(npwp); extras.npwp += 1; }
      continue;
    }

    // Named row -> a new shop.
    const shop = {
      sheet: sheetName,
      row: excelRow,
      name,
      addresses: address ? [address] : [],
      sales,
      ekspedisi: ekspedisi ? [ekspedisi] : [],
      contactPerson: contactPerson ? [contactPerson] : [],
      email: email ? [email] : [],
      keterangan: keterangan ? [keterangan] : [],
      city: deriveCity(address),
      sourceRows: [excelRow],
    };
    if (hasNpwp) shop.npwp = npwp ? [npwp] : [];
    shops.push(shop);
    current = shop;

    if (!salesEval.usable) {
      unusableSalesCount += 1;
      if (salesEval.reason === "blank") unusableSalesByReason.blank += 1;
      else if (salesEval.reason === "error-cell") unusableSalesByReason.errorCell += 1;
      else if (salesEval.reason === "bad-token") unusableSalesByReason.badToken += 1;
      const reasonText = {
        blank: "selnya kosong",
        "error-cell": `selnya berisi galat Excel (${salesEval.found})`,
        "bad-token": `isinya "${salesEval.found}", bukan nama Sales yang valid`,
      }[salesEval.reason];
      problems.push({
        severity: "warning",
        type: "unusable-sales",
        sheet: sheetName,
        row: excelRow,
        shopName: name,
        reason: salesEval.reason,
        found: salesEval.found,
        message: `Toko "${name}" (baris ${excelRow}) tidak punya nilai Sales yang bisa dipakai — ${reasonText}.`,
      });
    }
  }

  for (const shop of shops) {
    if (shop.addresses.length === 0) {
      missingAddressCount += 1;
      problems.push({
        severity: "warning", type: "missing-address", sheet: sheetName, row: shop.row, shopName: shop.name,
        message: `Toko "${shop.name}" (baris ${shop.row}) tidak punya alamat sama sekali.`,
      });
    }
    if (!shop.city.derived) {
      problems.push({
        severity: "warning", type: "city-not-derived", sheet: sheetName, row: shop.row, shopName: shop.name,
        address: shop.addresses[0] || "",
        message: `Kota untuk toko "${shop.name}" (baris ${shop.row}) tidak bisa diturunkan dari alamat.`,
      });
    }
  }

  // Duplicate-name detection: exact (normalized) name match only — see the file header comment
  // for why this deliberately does not reuse contactMatch.js's fuzzy scorer.
  const groups = new Map();
  for (const shop of shops) {
    const key = normalizeShopName(shop.name);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(shop);
  }
  let duplicateNameGroups = 0, dupIdentical = 0, dupDiffer = 0;
  const addressKey = (s) => s.addresses.map((a) => a.trim().replace(/\s+/g, " ")).join("|");
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    duplicateNameGroups += 1;
    const first = addressKey(group[0]);
    const identical = group.every((s) => addressKey(s) === first);
    if (identical) dupIdentical += 1; else dupDiffer += 1;
    problems.push({
      severity: "warning",
      type: "duplicate-name",
      sheet: sheetName,
      name: group[0].name,
      rows: group.map((s) => s.row),
      addressesMatch: identical,
      message: `Nama toko "${group[0].name}" muncul ${group.length} kali di sheet "${sheetName}" (baris ${group.map((s) => s.row).join(", ")}) — alamat ${identical ? "sama" : "berbeda"}.`,
    });
  }

  // Sales usage: distinct raw values merged case-insensitively, counted per SHOP (never per
  // row) — a shop's blank-row fill-down repeats of its own Sales are not real occurrences (see
  // the file header comment) and must never inflate this count.
  const salesMap = new Map();
  for (const shop of shops) {
    if (!shop.sales) continue;
    const key = shop.sales.replace(/\s+/g, " ").toUpperCase();
    if (!salesMap.has(key)) salesMap.set(key, { name: shop.sales, count: 0 });
    salesMap.get(key).count += 1;
  }
  const salesUsage = [...salesMap.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const stats = {
    rowCount: dataRows.length,
    shopCount: shops.length,
    blankRowCount,
    orphanBlankRowCount,
    extras,
    missingAddressCount,
    unusableSalesCount,
    unusableSalesByReason,
    cityDerived: shops.filter((s) => s.city.derived).length,
    cityNotDerived: shops.filter((s) => !s.city.derived).length,
    duplicateNameGroups,
    duplicateNameGroupsIdenticalAddress: dupIdentical,
    duplicateNameGroupsDifferingAddress: dupDiffer,
    salesUsage,
  };

  return { shops, problems, stats };
}

/**
 * Parses a contacts workbook's CABANG and PUSAT sheets into structured shop rows plus problems.
 * Pure, deterministic, no I/O. Any sheet in `sheets` other than CABANG/PUSAT (matched trim +
 * case-insensitive) is ignored — this is an allow-list of exactly two sheets, not a deny-list.
 *
 * @param {{name: string, rows: any[][]}[]} sheets
 * @returns {{
 *   sheets: {
 *     CABANG: {shops: object[], stats: object} | null,
 *     PUSAT: {shops: object[], stats: object} | null,
 *   },
 *   problems: object[],
 * }}
 *   A sheet is `null` when it's missing or its columns don't match — see the `column-mismatch`/
 *   `missing-sheet` problems (severity `"error"`) for exactly why. Every other problem is
 *   `severity:"warning"` — a data-quality issue inside an otherwise-valid structure, never a
 *   reason this function refuses to return shops for the other, valid sheet.
 */
export function parseContactWorkbook(sheets) {
  const bySheetName = new Map();
  for (const s of sheets || []) {
    bySheetName.set(cellText(s && s.name).toUpperCase(), s);
  }

  const problems = [];
  const result = { sheets: { CABANG: null, PUSAT: null }, problems };

  for (const sheetKey of Object.keys(SHEET_DEFS)) {
    const columns = SHEET_DEFS[sheetKey];
    const found = bySheetName.get(sheetKey);
    if (!found) {
      problems.push({
        severity: "error",
        type: "missing-sheet",
        sheet: sheetKey,
        message: `Sheet "${sheetKey}" tidak ditemukan di file.`,
      });
      continue;
    }
    const { shops, problems: sheetProblems, stats } = parseContactSheet(sheetKey, found.rows || [], columns);
    result.sheets[sheetKey] = shops === null ? null : { shops, stats };
    problems.push(...sheetProblems);
  }

  return result;
}

export { CABANG_COLUMNS, PUSAT_COLUMNS };
